import { useMemo } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import type { Row } from '../api';
import { fmt } from '../lib/format';

type Props = {
  rows: Row[];
  dimensionKey: keyof Row;
  metricKey: keyof Row;
  metricLabel: string;
  topN?: number;
};

const STACK_COLORS = [
  '#7c3aed', // violet
  '#6366f1', // indigo
  '#a855f7', // violet-2
  '#4c1d95', // deep
  '#ec4899', // pink
  '#3b82f6', // blue
  '#06b6d4', // cyan
  '#10b981', // emerald
  '#f59e0b', // amber
  '#ef4444', // red
  '#8b5cf6', // light violet
  '#0ea5e9', // sky
];
const OTHER_COLOR = '#9ca3af';

export function BarChartView({ rows, dimensionKey, metricKey, metricLabel, topN = 10 }: Props) {
  const { data, series } = useMemo(() => {
    // bucket by date → dim → metric sum
    const byDate = new Map<string, Map<string, number>>();
    const dimTotals = new Map<string, number>();

    for (const r of rows) {
      const date = String((r.date_start as any) ?? '—') || '—';
      const dim = String((r[dimensionKey] as any) ?? '—') || '—';
      const val = Number(r[metricKey] as any) || 0;

      let dimMap = byDate.get(date);
      if (!dimMap) {
        dimMap = new Map();
        byDate.set(date, dimMap);
      }
      dimMap.set(dim, (dimMap.get(dim) || 0) + val);
      dimTotals.set(dim, (dimTotals.get(dim) || 0) + val);
    }

    const topDims = Array.from(dimTotals.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, topN)
      .map(([d]) => d);
    const topSet = new Set(topDims);
    const hasOther = Array.from(dimTotals.keys()).some((d) => !topSet.has(d));

    const dates = Array.from(byDate.keys()).sort();
    const data = dates.map((date) => {
      const dimMap = byDate.get(date)!;
      const row: Record<string, number | string> = { date };
      let other = 0;
      for (const [dim, val] of dimMap) {
        if (topSet.has(dim)) row[dim] = (Number(row[dim]) || 0) + val;
        else other += val;
      }
      if (hasOther) row['Outros'] = other;
      return row;
    });

    const series = topDims.map((d, i) => ({
      key: d,
      label: d.length > 28 ? d.slice(0, 26) + '…' : d,
      color: STACK_COLORS[i % STACK_COLORS.length],
    }));
    if (hasOther) series.push({ key: 'Outros', label: 'Outros', color: OTHER_COLOR });

    return { data, series };
  }, [rows, dimensionKey, metricKey, topN]);

  const isCurrency = ['spend', 'cpa', 'cpc', 'cpm', 'conversion_value'].includes(String(metricKey));
  const isPct = ['ctr', 'conversion_rate'].includes(String(metricKey));
  const formatter = (v: number) => (isCurrency ? fmt.brl(v) : isPct ? fmt.pct(v) : fmt.num(v));

  return (
    <div className="chart-wrap" data-testid="bar-chart">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} margin={{ top: 16, right: 16, left: 8, bottom: 80 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
          <XAxis dataKey="date" angle={-35} textAnchor="end" interval={0} tick={{ fontSize: 11 }} height={80} />
          <YAxis tick={{ fontSize: 11 }} tickFormatter={formatter} />
          <Tooltip formatter={(v: number) => formatter(v)} />
          <Legend wrapperStyle={{ fontSize: 11 }} />
          {series.map((s) => (
            <Bar key={s.key} dataKey={s.key} name={s.label} stackId="metric" fill={s.color} />
          ))}
        </BarChart>
      </ResponsiveContainer>
      <div className="chart-caption">{metricLabel} por dia · empilhado por {String(dimensionKey)}</div>
    </div>
  );
}
